import React, { useState } from "react";
import { UploadZone, DownloadButton, ResetButton, loadImage } from "./_shared";
import { X } from "lucide-react";

// Minimal PDF builder for images (no external deps).
// Embeds JPEG images. If PNG, we re-encode to JPEG for compatibility.
async function buildPdf(imageFiles) {
  const encoder = new TextEncoder();
  const chunks = [];
  let offset = 0;
  const offsets = [];
  const push = (str) => {
    const bytes = typeof str === "string" ? encoder.encode(str) : str;
    chunks.push(bytes); offset += bytes.length;
  };

  push("%PDF-1.4\n%\xE2\xE3\xCF\xD3\n");

  const jpegs = [];
  for (const f of imageFiles) {
    const { img } = await loadImage(f);
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.92));
    const buf = new Uint8Array(await blob.arrayBuffer());
    jpegs.push({ buf, w: canvas.width, h: canvas.height });
  }

  const objects = [];
  const addObj = (body) => { objects.push(body); return objects.length; };

  // Reserve slots
  const catalogId = 1;
  const pagesId = 2;
  const pageIds = [];
  const imageIds = [];
  const contentIds = [];

  // Pre-fill catalog, pages placeholders
  objects.push(null); // 1
  objects.push(null); // 2

  jpegs.forEach((j) => {
    imageIds.push(addObj(`<< /Type /XObject /Subtype /Image /Width ${j.w} /Height ${j.h} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${j.buf.length} >>\nstream\n__IMG__\nendstream`));
  });

  jpegs.forEach((j, i) => {
    const pw = 595, ph = 842; // A4 in points
    const scale = Math.min(pw / j.w, ph / j.h);
    const dw = j.w * scale, dh = j.h * scale;
    const x = (pw - dw) / 2, y = (ph - dh) / 2;
    const stream = `q\n${dw} 0 0 ${dh} ${x} ${y} cm\n/Im${i} Do\nQ\n`;
    contentIds.push(addObj(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`));
    pageIds.push(addObj(`<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${pw} ${ph}] /Resources << /XObject << /Im${i} ${imageIds[i]} 0 R >> >> /Contents ${contentIds[i]} 0 R >>`));
  });

  objects[catalogId - 1] = `<< /Type /Catalog /Pages ${pagesId} 0 R >>`;
  objects[pagesId - 1] = `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageIds.length} >>`;

  // Serialize objects
  objects.forEach((body, idx) => {
    const id = idx + 1;
    offsets[id] = offset;
    if (body.includes("__IMG__")) {
      const [before, after] = body.split("__IMG__");
      push(`${id} 0 obj\n${before}`);
      push(jpegs[imageIds.indexOf(id)].buf);
      push(`${after}\nendobj\n`);
    } else {
      push(`${id} 0 obj\n${body}\nendobj\n`);
    }
  });

  const xrefOffset = offset;
  push(`xref\n0 ${objects.length + 1}\n`);
  push("0000000000 65535 f \n");
  for (let i = 1; i <= objects.length; i++) {
    push(`${String(offsets[i]).padStart(10, "0")} 00000 n \n`);
  }
  push(`trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);

  return new Blob(chunks, { type: "application/pdf" });
}

export default function ImageToPDF() {
  const [files, setFiles] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  const build = async (list) => {
    if (list.length === 0) return;
    setBusy(true);
    const blob = await buildPdf(list);
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(URL.createObjectURL(blob));
    setBusy(false);
  };

  const handleFiles = async (fs) => { const next = [...files, ...fs]; setFiles(next); await build(next); };
  const remove = async (i) => { const next = files.filter((_, idx) => idx !== i); setFiles(next); if (next.length) await build(next); else setPdfUrl(null); };
  const reset = () => { setFiles([]); setPdfUrl(null); };

  if (files.length === 0) return <UploadZone multiple onFiles={handleFiles} hint="Add multiple images to combine into one PDF" />;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {files.map((f, i) => (
          <div key={i} className="relative rounded-xl border border-slate-200 dark:border-white/10 overflow-hidden">
            <img src={URL.createObjectURL(f)} alt={f.name} className="w-full h-32 object-cover" />
            <button onClick={() => remove(i)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-white grid place-items-center hover:bg-red-600 transition-colors" aria-label="Remove"><X size={14} /></button>
            <div className="px-2 py-1 text-xs text-slate-500 dark:text-slate-400 truncate">{f.name}</div>
          </div>
        ))}
      </div>
      <UploadZone multiple onFiles={handleFiles} hint="Add more images" />
      <div className="flex items-center gap-3">
        <DownloadButton href={pdfUrl} filename="imagekithub.pdf">{busy ? "Building PDF…" : "Download PDF"}</DownloadButton>
        <ResetButton onClick={reset} />
      </div>
    </div>
  );
}
